// "How it works", animated (one 14s loop, CSS keyframes on inline SVG — see webUploader/AnimatedFigure):
//   1. a photo lands on the timeline at "Uploaded" and its face is outlined (face data is created);
//   2. it travels to "12 months" while the line fills; a buyer's selfie finds it twice ("Match");
//   3. at 12 months the face outline and the face-template chip dissolve ("Face data deleted");
//   4. the photo carries on to "Photo stays", where a bib search still finds it.
// Reduced motion: frozen at step 4 (face data deleted, the photo still there and found by bib).
// The runner is drawn (no photo file), so the face outline always sits on a face.
import type { CSSProperties } from "react";
import { AnimatedFigure } from "@/components/webUploader/AnimatedFigure";
import { EASE, kf, tr, type Stop } from "@/components/webUploader/keyframes";
import type { FaceDataCopy } from "@/content/faceData2026";
import { Runner, TemplateDots, scatter } from "./scene";

const SCOPE = "fdtl";
const DUR = 14;
const FREEZE = 84;

// ---- geometry (viewBox 400 × 268: the bottom strip is left clear for the pause button, which
// is a fixed 32px + 12px and would cover "Photo stays" on a 390px phone with less) -------------------------------------------------------------
const TRACK_Y = 176;
const A = 40;   // Uploaded
const B = 250;  // 12 months
const C = 348;  // Photo stays
const CARD = { w: 84, h: 72, y: 60 };
const cardX = (pin: number) => Math.max(6, Math.min(400 - CARD.w - 6, pin - CARD.w / 2));

// ---- timeline (% of the loop) -----------------------------------------------------------------
const IN = 3;          // photo appears
const FACE = 6;        // face outlined
const GO = 9;          // starts travelling
const AT_B = 52;       // reaches 12 months
const DEL = [55, 63] as const;  // dissolve
const AT_C = 74;       // reaches "Photo stays"
const OUT = 95;        // fade out, then the loop restarts

const name = (el: string) => `${SCOPE}-${el}`;
const anim = (el: string): CSSProperties => ({ animationName: name(el) });

const windowed = (el: string, from: number, to: number, fade = 1.5): string =>
  kf(name(el), [[0, "opacity:0"], [from - fade, "opacity:0"], [from, "opacity:1"], [to, "opacity:1"], [to + fade, "opacity:0"], [100, "opacity:0"]]);

const along = (el: string, x: (pin: number) => number, y: number): string =>
  kf(name(el), [
    [0, `${tr(x(A), y)};opacity:0`],
    [IN, `${tr(x(A), y)};opacity:1`],
    [GO, `${tr(x(A), y)};opacity:1;${EASE}`],
    [AT_B, `${tr(x(B), y)};opacity:1`],
    [DEL[1] + 1, `${tr(x(B), y)};opacity:1;${EASE}`],
    [AT_C, `${tr(x(C), y)};opacity:1`],
    [OUT, `${tr(x(C), y)};opacity:1`],
    [OUT + 3, `${tr(x(C), y)};opacity:0`],
    [100, `${tr(x(A), y)};opacity:0`],
  ]);

const BUBBLES = [
  { id: "s1", from: 18, to: 29 },
  { id: "s2", from: 34, to: 45 },
] as const;
const BIB = { from: 78, to: OUT } as const;

function buildKeyframes(): string {
  const out: string[] = [];
  out.push(along("card", cardX, CARD.y));
  out.push(along("pin", (p) => p, TRACK_Y));
  // the line fills from Uploaded to 12 months as the photo travels
  out.push(kf(name("fill"), [[0, "transform:scaleX(0)"], [GO, `transform:scaleX(0);${EASE}`], [AT_B, "transform:scaleX(1)"], [OUT + 2, "transform:scaleX(1)"], [OUT + 3, "transform:scaleX(0)"], [100, "transform:scaleX(0)"]]));
  // face data: the outline and the template chip, then the dissolve
  out.push(kf(name("face"), [[0, "opacity:0"], [FACE - 1, "opacity:0"], [FACE, "opacity:1"], [DEL[0], "opacity:1"], [DEL[0] + 4, "opacity:0"], [100, "opacity:0"]]));
  out.push(kf(name("chip"), [[0, "opacity:0"], [FACE - 1, "opacity:0"], [FACE, "opacity:1"], [DEL[0] + 2, "opacity:1"], [DEL[1], "opacity:0"], [100, "opacity:0"]]));
  scatter(9).forEach(([dx, dy], i) => {
    out.push(kf(name(`d${i}`), [
      [0, "transform:translate(0px,0px);opacity:0"],
      [FACE - 1, "transform:translate(0px,0px);opacity:0"],
      [FACE, "transform:translate(0px,0px);opacity:1"],
      [DEL[0] + i * 0.4, `transform:translate(0px,0px);opacity:1;${EASE}`],
      [DEL[1] + i * 0.4, `transform:translate(${dx}px,${dy}px);opacity:0`],
      [100, `transform:translate(${dx}px,${dy}px);opacity:0`],
    ] as Stop[]));
  });
  out.push(windowed("deleted", DEL[0] + 2, OUT));
  // selfie search finds it (twice) while inside the 12 months
  for (const b of BUBBLES) {
    out.push(windowed(b.id, b.from, b.to, 1));
    out.push(windowed(`${b.id}ok`, b.from + 3, b.to, 0.8));
  }
  // after: bib search still finds it
  out.push(windowed("bib", BIB.from, BIB.to, 1));
  out.push(windowed("bibok", BIB.from + 3, BIB.to, 0.8));
  return out.join("");
}

const KEYFRAMES = buildKeyframes();

/** A search bubble at the top of the scene, with a dashed line down towards the track. */
const Bubble = ({ id, x, label, kind }: { id: string; x: number; label: string; kind: "selfie" | "bib" }) => (
  <g data-a="" style={anim(id)}>
    <rect x={x} y={8} width={92} height={30} rx={15} className="fill-card stroke-border" strokeWidth={1} />
    {kind === "selfie" ? (
      // a tiny phone with a face
      <g transform={`translate(${x + 10},${13})`}>
        <rect width={13} height={20} rx={3} className="fill-foreground" />
        <circle cx={6.5} cy={9} r={3.4} className="fill-primary" />
      </g>
    ) : (
      // a magnifier
      <g transform={`translate(${x + 10},${14})`} className="stroke-foreground" fill="none" strokeWidth={2}>
        <circle cx={7} cy={7} r={5.5} />
        <line x1={11} y1={11} x2={16} y2={16} strokeLinecap="round" />
      </g>
    )}
    <text x={x + 30} y={27.5} fontSize={11} fontWeight={600} className="fill-foreground">{label}</text>
  </g>
);

/** "✓ Match" on the card. */
const Match = ({ id, label }: { id: string; label: string }) => {
  const w = 24 + label.length * 5.8;
  const x = CARD.w + 4 - w;
  return (
    <g data-a="" style={anim(id)}>
      <rect x={x} y={CARD.h - 12} width={w} height={18} rx={9} className="fill-emerald-600" />
      <path d={`M ${x + 6} ${CARD.h - 3} l 3 3 l 5 -6`} stroke="white" strokeWidth={1.8} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <text x={x + 18} y={CARD.h + 1.5} fontSize={9.5} fontWeight={700} fill="white">{label}</text>
    </g>
  );
};

export const RetentionTimeline = ({ copy, pauseLabel, playLabel }: { copy: FaceDataCopy["timeline"]; pauseLabel: string; playLabel: string }) => {
  const s = copy.scene;
  return (
    <AnimatedFigure
      scope={SCOPE}
      durationS={DUR}
      freezeAt={FREEZE}
      keyframes={KEYFRAMES}
      label={copy.label}
      description={copy.caption}
      pauseLabel={pauseLabel}
      playLabel={playLabel}
      className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-6"
    >
      <svg viewBox="0 0 400 268" className="block h-auto w-full" role="presentation" data-testid="retention-timeline">
        {/* the track: Uploaded → 12 months (fills) → Photo stays */}
        <line x1={A} y1={TRACK_Y} x2={C} y2={TRACK_Y} className="stroke-border" strokeWidth={4} strokeLinecap="round" />
        <rect
          data-a=""
          x={A}
          y={TRACK_Y - 2}
          width={B - A}
          height={4}
          rx={2}
          className="fill-primary"
          style={{ ...anim("fill"), transformBox: "fill-box", transformOrigin: "left center" }}
        />
        {[A, B, C].map((x) => (
          <circle key={x} cx={x} cy={TRACK_Y} r={5} className={x === B ? "fill-primary" : "fill-muted-foreground"} />
        ))}
        <text x={(A + B) / 2} y={TRACK_Y - 10} fontSize={12} textAnchor="middle" className="fill-muted-foreground">{s.search}</text>
        <text x={A} y={TRACK_Y + 20} fontSize={12.5} fontWeight={600} textAnchor="middle" className="fill-foreground">{s.upload}</text>
        <text x={B} y={TRACK_Y + 20} fontSize={12.5} fontWeight={600} textAnchor="middle" className="fill-foreground">{s.months}</text>
        <text x={C} y={TRACK_Y + 20} fontSize={12.5} fontWeight={600} textAnchor="middle" className="fill-foreground">{s.carry}</text>
        <g data-a="" style={anim("deleted")}>
          <text x={B} y={TRACK_Y + 38} fontSize={12} fontWeight={700} textAnchor="middle" style={{ fill: "hsl(var(--primary-text))" }}>{s.deleted}</text>
        </g>

        {/* the moving pin on the track */}
        <g data-a="" style={anim("pin")}>
          <circle r={8} className="fill-primary" opacity={0.25} />
          <circle r={4.5} className="fill-primary" />
        </g>

        {/* searches */}
        <Bubble id="s1" x={292} label={s.selfie} kind="selfie" />
        <Bubble id="s2" x={292} label={s.selfie} kind="selfie" />
        <Bubble id="bib" x={292} label="1234" kind="bib" />

        {/* the photo card, travelling with the pin */}
        <g data-a="" style={anim("card")}>
          <Runner w={CARD.w} h={CARD.h} clipId={`${SCOPE}-clip`} faceStyle={anim("face")} />
          {/* face-template chip, which dissolves at 12 months */}
          <g transform={`translate(${CARD.w - 18},${-12})`}>
            <g data-a="" style={anim("chip")}>
              <rect width={28} height={28} rx={7} className="fill-card stroke-primary" strokeWidth={1.5} />
            </g>
            <TemplateDots cols={3} rows={3} x={7} y={7} gap={7} r={1.9} dotStyle={(i) => anim(`d${i}`)} />
          </g>
          <Match id="s1ok" label={s.match} />
          <Match id="s2ok" label={s.match} />
          <Match id="bibok" label={s.match} />
        </g>
      </svg>
    </AnimatedFigure>
  );
};

export default RetentionTimeline;
